import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, LogIn } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(identifier, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, Dr. ${res.user.name}`);
      navigate("/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Log in with your User ID or email to continue your NEET PG prep."
      footer={
        <span>
          New to DearDr?{" "}
          <Link to="/register" className="text-blue-700 font-semibold hover:underline" data-testid="login-link-register">
            Create an account
          </Link>
        </span>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="identifier">User ID or Email</Label>
          <Input
            id="identifier"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="DDR-XXXXXX or you@example.com"
            required
            className="mt-1.5 h-11"
            data-testid="login-input-identifier"
            autoComplete="username"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-blue-700 font-semibold hover:underline" data-testid="login-link-forgot">
              Forgot password?
            </Link>
          </div>
          <div className="relative mt-1.5">
            <Input
              id="password"
              type={show ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="h-11 pr-10"
              data-testid="login-input-password"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
              aria-label="Toggle password"
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          data-testid="login-submit-btn"
          className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold"
        >
          <LogIn className="h-4 w-4 mr-2" />
          {loading ? "Logging in…" : "Log in"}
        </Button>
      </form>
    </AuthLayout>
  );
}
